import { LocalCliError } from "./errors.js";
import type { ReleaseManifest } from "./release-manifest.js";

/**
 * The two things the updater job must have before it applies anything.
 *
 * Both used to be graceful fallbacks, and both fell back to reading the PLATFORM
 * REPOSITORY — a tarball when the baked content was missing, a git tree when the manifest
 * did not name one. That repository is private and the job holds no token, so neither
 * fallback could ever succeed; they only turned a legible precondition failure into a 404
 * inside a customer's updater at three in the morning.
 *
 * They live here, shared, because there are TWO paths that need them and they were found
 * one at a time: the forward converge, and the rollback that runs when a forward apply
 * fails the health gate. The rollback's own doc comment claimed "zero repo auth" while
 * doing exactly the same thing. Two copies of a precondition is how one of them keeps
 * the behaviour the other gave up.
 *
 * Scope: this is the RAIL's contract. An interactive run by a maintainer who does have
 * repository access is a different path and refuses differently — see
 * `release-content.ts`.
 */

/**
 * The job applies content baked into its own image, resolved through the repo-root
 * marker every installer image writes at build time. No marker means the image is
 * malformed, not that content should be fetched from somewhere else.
 */
export function assertBakedContentPresent(repoRoot: string | null): asserts repoRoot is string {
  if (repoRoot !== null) return;
  throw new LocalCliError({
    code: "UPDATER_IMAGE_MALFORMED",
    message:
      "the updater job found no ~/.m8t/repo-root marker, so it has no baked release content. " +
      "Every installer image writes that marker at build time, so this image is malformed.",
    hint:
      "Re-pull the updater job's image from the installer reference in the target manifest — the " +
      "job self-updates to it on the next tick — or reinstall the platform. This job never fetches " +
      "content from the platform repository: that repository is private and the job holds no token.",
  });
}

/**
 * Every manifest the current generator cuts carries `components.infra.treeSha`, which is
 * what the job diffs infra against. One that does not predates it and cannot be a
 * forward target; reading the tree instead would mean reading the platform repository.
 *
 * Verified against the published channel when this was written: 0.6.2, 0.6.3 and 0.6.4 —
 * every release a live installation could roll back to — all carry it. So refusing costs
 * nothing that exists, which is the only reason it is safe to refuse on the rollback path
 * as well as the forward one.
 */
export function assertForwardTarget(manifest: ReleaseManifest): void {
  if (typeof manifest.components.infra?.treeSha === "string" && manifest.components.infra.treeSha.length > 0) return;
  throw new LocalCliError({
    code: "MANIFEST_NOT_A_FORWARD_TARGET",
    message:
      `manifest ${manifest.platform.version} carries no components.infra.treeSha, so the updater job ` +
      "cannot determine the infra target without reading the platform repository.",
    hint:
      "Cuts from 0.7.0 onward always carry infra.treeSha. Target a current release: a manifest this " +
      "old is not a valid target for an automatic update.",
  });
}
